import { entityAssignedToLocation, normalizeLocationIds } from '../locationTypes';

export const staffWorksAtLocation = (staffMember, locationId) =>
  entityAssignedToLocation(staffMember, locationId);

export const serviceOfferedAtLocation = (service, locationId) =>
  entityAssignedToLocation(service, locationId);

export const getStaffAtLocation = (staff = [], locationId) =>
  staff.filter((member) => staffWorksAtLocation(member, locationId));

export const getServicesAtLocation = (services = [], locationId) =>
  services.filter((service) => serviceOfferedAtLocation(service, locationId));

export const resolveEffectiveLocationIds = (locationIds = [], locations = []) => {
  const activeLocations = locations.filter((location) => location.active !== false);
  const normalized = normalizeLocationIds(locationIds);

  if (normalized.length === 0) {
    return activeLocations.map((location) => location.id);
  }

  return normalized;
};

export const getBookableStaffForService = ({
  service,
  staff = [],
  locationIds = [],
  locations = [],
}) => {
  const assignedIds = normalizeLocationIds(service?.employeeIds);
  const assignedStaff = staff.filter((member) => assignedIds.includes(member.id));
  const effectiveLocationIds = resolveEffectiveLocationIds(locationIds, locations);

  if (effectiveLocationIds.length === 0) {
    return assignedStaff;
  }

  return assignedStaff.filter((member) =>
    effectiveLocationIds.some((locationId) => staffWorksAtLocation(member, locationId))
  );
};

export const staffWorksAtAnyLocation = (staffMember, locationIds = [], locations = []) => {
  const effectiveLocationIds = resolveEffectiveLocationIds(locationIds, locations);
  if (effectiveLocationIds.length === 0) return true;
  return effectiveLocationIds.some((locationId) => staffWorksAtLocation(staffMember, locationId));
};

export const formatLocationScope = (locationIds = [], locations = []) => {
  const activeLocations = locations.filter((location) => location.active !== false);
  const normalized = normalizeLocationIds(locationIds);

  if (activeLocations.length <= 1) {
    return { shortLabel: null, names: activeLocations.map((location) => location.name) };
  }

  if (normalized.length === 0 || normalized.length >= activeLocations.length) {
    return {
      shortLabel: 'All locations',
      names: activeLocations.map((location) => location.name),
    };
  }

  const names = normalized
    .map((id) => activeLocations.find((location) => location.id === id)?.name)
    .filter(Boolean);

  return {
    shortLabel: names.length === 1 ? names[0] : `${names.length} locations`,
    names,
  };
};

export const buildLocationCoverage = ({ location, staff = [], services = [] }) => {
  const staffAtLocation = getStaffAtLocation(staff, location.id);
  const servicesAtLocation = getServicesAtLocation(services, location.id);

  const serviceSummaries = servicesAtLocation.map((service) => {
    const assignedIds = normalizeLocationIds(service.employeeIds);
    const assignedStaff = staff.filter((member) => assignedIds.includes(member.id));
    const bookableStaff = getBookableStaffForService({
      service,
      staff,
      locationIds: [location.id],
      locations: [location],
    });

    return {
      service,
      assignedStaff,
      bookableStaff,
      isBookable: bookableStaff.length > 0,
    };
  });

  return {
    staffAtLocation,
    servicesAtLocation,
    serviceSummaries,
    unbookableServices: serviceSummaries.filter((summary) => !summary.isBookable),
  };
};
